import * as CONSTANT from '../constants';

/**
 * RECENTLY VISITED ITEMS TRACKING SYSTEM
 * 
 * This module tracks user visits to items and stores them in BOTH cookies AND localStorage 
 * for Optimizely integration flexibility.
 * 
 * BEHAVIOR:
 * - Stores last 5 visited items only
 * - Latest visited items appear first: [newest, ..., oldest]  
 * - When 6th item is visited, oldest item is removed
 * - Example: [1,2,3,4,5] → visit 6,7 → [7,6,5,4,3]
 * 
 * DUAL STORAGE APPROACH:
 * - Cookie Name: 'cafe_zupas_recent_items' (for Optimizely cookie access)
 * - localStorage Key: 'cafe_zupas_recent_items_ls' (for Optimizely localStorage access)
 * - Data synchronized between both storage methods
 * - Reads localStorage first (more reliable), falls back to cookie
 * 
 * DATA IMPROVEMENTS:
 * - All IDs are numbers (not strings)  
 * - All image URLs are full URLs (not relative)
 * - All item URLs are full URLs (not relative)
 * - All items have category_name (including combos)
 * 
 * EXAMPLE STORAGE CONTENT:
 * [
 *   {
 *     "id": 123,
 *     "name": "MEDITERRANEAN BOWL",
 *     "category_name": "Bowls",
 *     "category_id": 5,
 *     "image": "https://cloudfront.url/Items/123/item-123.webp?v=1234567890",
 *     "itemType": "single", 
 *     "itemUrl": "http://localhost:3000/order/123?category_id=5",
 *     "visitedAt": 1709078400000,
 *     "pageOpenedAt": "2024-02-27T10:30:00.000Z"
 *   },
 *   {
 *     "id": 93,
 *     "name": "TRY 2 COMBO",
 *     "category_name": "Combos",
 *     "image": "https://cloudfront.url/Items/93/item-93.webp?v=1234567890",
 *     "itemType": "try2combo",
 *     "itemUrl": "http://localhost:3000/menu/Try%202%20Combo",
 *     "visitedAt": 1709078200000,
 *     "pageOpenedAt": "2024-02-27T10:27:00.000Z"
 *   }
 * ]
 * 
 * COOKIE SETTINGS:
 * - SameSite=Lax (accessible to Optimizely during navigation)
 * - 30-day expiry
 * - Secure flag in production
 * - No HttpOnly flag (JavaScript accessible)
 * 
 * LOCALSTORAGE BENEFITS:
 * - 5-10MB storage limit (vs 4KB cookie limit)
 * - No network transmission overhead
 * - Better performance and privacy
 */

export interface RecentlyVisitedItem {
  id: number; // Always number, not string
  name: string;
  category_name?: string; // Required for all items including combos
  category_id?: string | number;
  image?: string; // Full CloudFront image URL
  visitedAt: number; // timestamp when item was visited
  itemType?: 'single' | 'try2combo' | 'kidscombo';
  itemUrl?: string; // Full URL of the item page
  pageOpenedAt: string; // UTC timestamp when page was opened (ISO format)
}

const COOKIE_NAME = 'cafe_zupas_recent_items';
const LOCALSTORAGE_KEY = 'cafe_zupas_recent_items'; // Same key for consistency
const MAX_RECENT_ITEMS = 5; // Keep only last 5 items
const COOKIE_EXPIRY_DAYS = 30;

/**
 * Utility functions for dual storage (Cookie + localStorage)
 */
const setDataInBothStorage = (items: RecentlyVisitedItem[]) => {
  const jsonData = JSON.stringify(items);
  
  // 1. Set in localStorage
  try {
    localStorage.setItem(LOCALSTORAGE_KEY, jsonData);
  } catch (error) {
    console.warn('Error setting localStorage:', error);
  }
  
  // 2. Set in cookie
  try {
    const expires = new Date();
    expires.setDate(expires.getDate() + COOKIE_EXPIRY_DAYS);
    const cookieString = `${COOKIE_NAME}=${encodeURIComponent(jsonData)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax${process.env.NODE_ENV === 'production' ? '; Secure' : ''}`;
    document.cookie = cookieString;
  } catch (error) {
    console.warn('Error setting cookie:', error);
  }
};

const getDataFromStorage = (): RecentlyVisitedItem[] => {
  // Try localStorage first (more reliable)
  try {
    const localData = localStorage.getItem(LOCALSTORAGE_KEY);
    if (localData) {
      const items = JSON.parse(localData);
      return Array.isArray(items) ? items : [];
    }
  } catch (error) {
    console.warn('Error reading from localStorage:', error);
  }
  
  // Fallback to cookie
  try {
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(COOKIE_NAME + '='))
      ?.split('=')[1];
      
    if (cookieValue) {
      const items = JSON.parse(decodeURIComponent(cookieValue));
      return Array.isArray(items) ? items : [];
    }
  } catch (error) {
    console.warn('Error reading from cookie:', error);
  }
  
  return [];
};

const clearBothStorage = () => {
  // Clear localStorage
  try {
    localStorage.removeItem(LOCALSTORAGE_KEY);
  } catch (error) {
    console.warn('Error clearing localStorage:', error);
  }
  
  // Clear cookie by setting expired date
  try {
    document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
  } catch (error) {
    console.warn('Error clearing cookie:', error);
  }
};

/**
 * Get full URL with domain, path and relevant query parameters
 * Returns: 'http://localhost:3000/order/307?category_id=3'
 */
const getFullUrl = (): string => {
  try {
    return window.location.href;
  } catch (error) {
    return '';
  }
};

/**
 * Get full image URL using CloudFront pattern
 * Returns: 'https://cloudfront.url/Items/123/item-123.webp?v=version'
 */
const getFullImageUrl = (itemId: number): string => {
  if (!itemId) return '';
  
  try {
    return `${CONSTANT.REACT_APP_CLOUD_FRONT}/Items/${itemId}/item-${itemId}.webp`;
  } catch (error) {
    console.warn('Error generating image URL:', error);
    return '';
  }
};

/**
 * Custom hook for managing recently visited items in BOTH cookies AND localStorage
 */
export const useRecentlyVisitedItems = () => {
  const getRecentItems = (): RecentlyVisitedItem[] => {
    return getDataFromStorage();
  };

  const addRecentItem = (item: Omit<RecentlyVisitedItem, 'visitedAt' | 'pageOpenedAt' | 'itemUrl'>) => {
    try {
      const currentItems = getRecentItems();
      
      // Remove existing item with same id if present
      const filteredItems = currentItems.filter(existing => Number(existing.id) !== Number(item.id));
      
      // Add new item at the beginning with current timestamps
      const newItem: RecentlyVisitedItem = {
        ...item,
        id: Number(item.id), // Ensure id is always a number
        image: getFullImageUrl(Number(item.id)), // Generate full CloudFront URL
        visitedAt: Date.now(),
        pageOpenedAt: new Date().toISOString(), // UTC timestamp in ISO format
        itemUrl: getFullUrl() // Auto-generate full URL
      };
      
      const updatedItems = [newItem, ...filteredItems].slice(0, MAX_RECENT_ITEMS);
      
      // Save to BOTH storage methods
      setDataInBothStorage(updatedItems);
    } catch (error) {
      console.warn('Error saving recent item:', error);
    }
  };

  const removeRecentItem = (itemId: number) => {
    try {
      const currentItems = getRecentItems();
      const filteredItems = currentItems.filter(item => Number(item.id) !== Number(itemId));
      
      // Save to BOTH storage methods
      setDataInBothStorage(filteredItems);
    } catch (error) {
      console.warn('Error removing recent item:', error);
    }
  };

  const clearRecentItems = () => {
    clearBothStorage();
  };

  return {
    recentItems: getRecentItems(),
    addRecentItem,
    removeRecentItem,
    clearRecentItems
  };
};

/**
 * Utility function to track item visit without hook (for use in class components or utilities)
 * Stores data in BOTH cookies AND localStorage
 */
export const trackItemVisit = (item: Omit<RecentlyVisitedItem, 'visitedAt' | 'pageOpenedAt' | 'itemUrl'>) => {
  try {
    // Get existing data from storage
    const currentItems = getDataFromStorage();

    // Remove existing item with same id if present
    const filteredItems = currentItems.filter(existing => existing.id !== item.id);
    
    // Add new item at the beginning with current timestamps
    const newItem: RecentlyVisitedItem = {
      ...item,
      id: Number(item.id), // Ensure id is always a number
      image: getFullImageUrl(Number(item.id)), // Generate full CloudFront URL
      visitedAt: Date.now(),
      pageOpenedAt: new Date().toISOString(), // UTC timestamp in ISO format
      itemUrl: getFullUrl() // Auto-generate full URL
    };
    
    // Keep only latest 5 items (newest first: [7,6,5,4,3])
    const updatedItems = [newItem, ...filteredItems].slice(0, MAX_RECENT_ITEMS);
    
    // Save to BOTH storage methods
    setDataInBothStorage(updatedItems);
  } catch (error) {
    console.warn('Error tracking item visit:', error);
  }
};

/**
 * Debug utility to test BOTH storage methods (for development/testing only)
 */
export const debugRecentItems = () => {
  console.log('=== RECENTLY VISITED ITEMS DEBUG (DUAL STORAGE) ===');
  
  // Check localStorage
  try {
    const localData = localStorage.getItem(LOCALSTORAGE_KEY);
    if (localData) {
      const items = JSON.parse(localData);
      console.log('📦 localStorage - Items count:', items.length);
      console.log('📦 localStorage - Items (newest first):', items);
      console.table(items);
    } else {
      console.log('📦 localStorage - No recent items found');
    }
  } catch (error) {
    console.error('📦 localStorage - Error:', error);
  }
  
  console.log('');
  
  // Check cookie
  try {
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(COOKIE_NAME + '='))
      ?.split('=')[1];
      
    if (cookieValue) {
      const items = JSON.parse(decodeURIComponent(cookieValue));
      console.log('🍪 Cookie - Items count:', items.length);
      console.log('🍪 Cookie - Items (newest first):', items);
      console.table(items);
    } else {
      console.log('🍪 Cookie - No recent items found');
    }
  } catch (error) {
    console.error('🍪 Cookie - Error:', error);
  }
  
  console.log('');
  console.log('📋 Raw cookie data:', document.cookie);
  console.log('🔄 Using getDataFromStorage():', getDataFromStorage());
  console.log('====================================================');
};

/**
 * Public API for Optimizely integration
 * Provides access to data from both storage methods
 */
export const getRecentlyVisitedItems = {
  // Get data from preferred storage (localStorage first, cookie fallback)
  fromBoth: (): RecentlyVisitedItem[] => getDataFromStorage(),
  
  // Get data specifically from localStorage
  fromLocalStorage: (): RecentlyVisitedItem[] => {
    try {
      const data = localStorage.getItem(LOCALSTORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  },
  
  // Get data specifically from cookie
  fromCookie: (): RecentlyVisitedItem[] => {
    try {
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith(COOKIE_NAME + '='))
        ?.split('=')[1];
      return cookieValue ? JSON.parse(decodeURIComponent(cookieValue)) : [];
    } catch {
      return [];
    }
  }
};